import { Component, ElementRef, afterRenderEffect, computed, inject, input, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { PageNoticeService } from './page-notice.service';

/**
 * An info note at the top of a page that the user can close. Pair it with
 * `<lib-page-notice-toggle>` (same `noticeId`) somewhere in the page header to reopen it.
 */
@Component({
  selector: 'lib-page-notice',
  imports: [MatButtonModule, MatIconModule, MatTooltipModule],
  template: `
    @if (!dismissed()) {
      <aside
        #notice
        class="page-notice"
        tabindex="-1"
        [attr.aria-label]="label()"
      >
        <mat-icon aria-hidden="true">info</mat-icon>
        <p><ng-content /></p>
        <button
          mat-icon-button
          class="page-notice-close"
          matTooltip="Dismiss"
          aria-label="Dismiss"
          (click)="dismiss()"
        >
          <mat-icon>close</mat-icon>
        </button>
      </aside>
    }
  `,
  styles: `
    .page-notice {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 4px 4px 4px 16px;
      border-left: 3px solid var(--page-notice-accent, var(--jtms-primary-dark, #181b88));
      background: var(--page-notice-tint, var(--jtms-primary-tint, #e4e4f5));
      font-size: 13px;
      line-height: 1.5;

      &:focus {
        outline: none;
      }

      &:focus-visible {
        outline: 2px solid var(--page-notice-accent, var(--jtms-primary-dark, #181b88));
        outline-offset: 2px;
      }

      > mat-icon {
        flex: none;
        color: var(--page-notice-accent, var(--jtms-primary-dark, #181b88));
      }

      p {
        flex: 1;
        margin: 0;
        padding: 8px 0;
      }
    }

    .page-notice-close {
      flex: none;
    }
  `,
})
export class PageNotice {
  readonly #notices = inject(PageNoticeService);

  /** Versioned id the closed state is stored under, e.g. `inventory-review.v1`. */
  readonly noticeId = input.required<string>();
  /** Accessible name for the note. */
  readonly label = input('About this page');

  protected readonly dismissed = computed(() => this.#notices.isDismissed(this.noticeId()));
  private readonly notice = viewChild<ElementRef<HTMLElement>>('notice');

  constructor() {
    afterRenderEffect(() => {
      const focus = this.#notices.focusTarget();
      const notice = this.notice();
      if (focus?.id !== this.noticeId() || focus.target !== 'notice' || !notice) return;
      notice.nativeElement.focus();
      this.#notices.focusTarget.set(null);
    });
  }

  protected dismiss(): void {
    this.#notices.dismiss(this.noticeId());
  }
}
